import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { GitBranch, Phone, Power, Star, Map as MapIcon, Warehouse, ShoppingCart } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  findWarehouse, findZone, branchesSourcingWarehouse,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

export const WhSourcingTab = ({ warehouseId, org }) => {
  const { version: dataVersion } = useSharedStore();
  const warehouse = useMemo(() => findWarehouse(warehouseId), [warehouseId, dataVersion]);
  const zone = useMemo(() => (warehouse?.zoneId ? findZone(warehouse.zoneId) : null), [warehouse, dataVersion]);
  const sourcingBranches = useMemo(() => branchesSourcingWarehouse(warehouseId), [warehouseId, dataVersion]);

  if (!warehouse) {
    return (
      <div className="py-16 text-center text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
        Warehouse not found.
      </div>
    );
  }

  const accent = org.theme.primary;

  return (
    <div className="flex flex-col gap-5">
      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[var(--theme-tertiary)] rounded-3xl p-6 shadow-sm"
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-extrabold tracking-tight text-[#0d0d0d] inline-flex items-center gap-2">
              <GitBranch size={16} style={{ color: accent }} aria-hidden="true" />
              Branch sourcing
            </h3>
            <p className="text-[11px] font-medium text-slate-500 mt-1">
              These branches pull their stock from <b className="text-[#0d0d0d]">{warehouse.name}</b>
              {zone && <> in zone <b className="text-[#0d0d0d]">{zone.name}</b></>}. Assignments are managed by the Org Admin.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: 'var(--theme-primary-soft)', color: accent }}>
            <Warehouse size={11} aria-hidden="true" /> {sourcingBranches.length} branch{sourcingBranches.length === 1 ? '' : 'es'}
          </span>
        </header>
      </motion.section>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sourcingBranches.map((b) => (
          <motion.article
            key={b.id}
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-5 flex flex-col gap-4"
          >
            <header className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white shrink-0 shadow-lg" style={{ background: accent, boxShadow: `0 12px 24px -10px ${accent}` }} aria-hidden="true">
                <GitBranch size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate">{b.name}</p>
                  {b.isMain && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold uppercase tracking-[0.12em] shrink-0" style={{ background: 'var(--theme-primary-soft)', color: accent }}>
                      <Star size={9} aria-hidden="true" /> Main
                    </span>
                  )}
                </div>
                <p className="text-[11px] font-medium text-slate-400 truncate mt-0.5">{b.address || '—'}</p>
                <span className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${b.status === 'Active' ? 'text-emerald-600' : 'text-rose-500'}`}>
                  <Power size={10} aria-hidden="true" /> {b.status}
                </span>
              </div>
            </header>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">District</dt>
                <dd className="font-medium text-slate-700 truncate">{b.district || '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Zipcode</dt>
                <dd className="font-medium text-slate-700">{b.zipcode || '—'}</dd>
              </div>
              <div className="col-span-2 inline-flex items-center gap-1 mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                <Phone size={11} aria-hidden="true" /> {b.phone || '—'}
                <span className="mx-1">·</span>
                <MapIcon size={11} aria-hidden="true" /> {(b.state || '—')}
              </div>
              <div className="col-span-2 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: b.purchaseEnabled ? accent : '#94a3b8' }}>
                <ShoppingCart size={11} aria-hidden="true" /> {b.purchaseEnabled ? 'Branch purchases on' : 'Branch purchases off'}
              </div>
            </dl>
          </motion.article>
        ))}
        {sourcingBranches.length === 0 && (
          <div className="col-span-full py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No branches source from this warehouse.</p>
            <p className="text-[11px] font-medium text-slate-500 mt-1">Org Admin can attach branches to this warehouse from the Branches tab.</p>
          </div>
        )}
      </section>
    </div>
  );
};

WhSourcingTab.propTypes = {
  warehouseId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
};
